import React, { useEffect, useRef, useState } from 'react';

const formatDateInput = (d) => d.toISOString().slice(0, 10);

const defaultMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    startDate: formatDateInput(firstDay),
    endDate: formatDateInput(lastDay),
  };
};

const ChatInChannelSearchPanel = ({
  isOpen,
  query,
  mentionsOnly,
  fromUserId,
  channelUsers = [],
  matchCount,
  activeMatchIndex,
  dateRangeLoading = false,
  onQueryChange,
  onMentionsOnlyChange,
  onFromUserIdChange,
  onPrevMatch,
  onNextMatch,
  onClearFilters,
  onDateRangeSearch,
  onClose,
}) => {
  const inputRef = useRef(null);
  const [showDateRange, setShowDateRange] = useState(false);
  const [startDate, setStartDate] = useState(() => defaultMonthRange().startDate);
  const [endDate, setEndDate] = useState(() => defaultMonthRange().endDate);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      const range = defaultMonthRange();
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      setShowDateRange(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const hasActiveSearch = Boolean(query.trim() || mentionsOnly || fromUserId);
  const showNavigation = hasActiveSearch && matchCount > 0;
  const currentPosition = showNavigation ? activeMatchIndex + 1 : 0;

  const handleDateRangeSubmit = (e) => {
    e.preventDefault();
    if (typeof onDateRangeSearch !== 'function') return;
    onDateRangeSearch({ startDate, endDate, userId: fromUserId || null });
  };

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80 px-3 py-2.5 sm:px-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="relative w-full max-w-[200px] sm:max-w-[240px] flex-shrink-0">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (e.shiftKey) {
                    onPrevMatch();
                  } else {
                    onNextMatch();
                  }
                }
                if (e.key === 'Escape') {
                  onClose();
                }
              }}
              placeholder="Search..."
              className="w-full pl-8 pr-2.5 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/60"
            />
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer select-none flex-shrink-0">
            <input
              type="checkbox"
              checked={mentionsOnly}
              onChange={(e) => onMentionsOnlyChange(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Your mentions</span>
          </label>

          <button
            type="button"
            onClick={() => setShowDateRange((open) => !open)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border transition-colors flex-shrink-0 ${
              showDateRange
                ? 'border-primary-400 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-950/40 dark:text-primary-300'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            aria-expanded={showDateRange}
            title="Search messages by date range"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="whitespace-nowrap">By date</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="ml-auto flex-shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200/70 dark:hover:bg-gray-700 transition-colors"
            title="Close search"
            aria-label="Close search"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">From</span>
            <select
              value={fromUserId || ''}
              onChange={(e) => onFromUserIdChange(e.target.value || null)}
              className="min-w-[10rem] max-w-[14rem] flex-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-1.5 px-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500/60"
              aria-label="Filter messages by sender"
            >
              <option value="">Anyone</option>
              {channelUsers.map((channelUser) => (
                <option key={channelUser.id} value={channelUser.id}>
                  {channelUser.label}
                </option>
              ))}
            </select>
            {hasActiveSearch && (
              <button
                type="button"
                onClick={onClearFilters}
                className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0 whitespace-nowrap"
              >
                Clear filter
              </button>
            )}
          </div>

          {hasActiveSearch && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
              {matchCount > 0 ? (
                <>
                  <span>{currentPosition} of {matchCount}</span>
                  <button
                    type="button"
                    onClick={onPrevMatch}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    title="Previous match (Shift+Enter)"
                    aria-label="Previous match"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={onNextMatch}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    title="Next match (Enter)"
                    aria-label="Next match"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              ) : (
                <span>No matches</span>
              )}
            </div>
          )}
        </div>

        {showDateRange && (
          <form
            onSubmit={handleDateRangeSubmit}
            className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-2.5"
          >
            <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
              Start date
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-1.5 px-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500/60"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
              End date
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-1.5 px-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500/60"
              />
            </label>
            <button
              type="submit"
              disabled={dateRangeLoading || !startDate || !endDate}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white transition-colors"
            >
              {dateRangeLoading ? 'Searching…' : 'Search dates'}
            </button>
            <p className="w-full text-[11px] text-gray-500 dark:text-gray-400">
              Loads messages in this range{fromUserId ? ' from the selected sender' : ''}.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChatInChannelSearchPanel;
